// Chat composer: growing textarea, attachment chips (thumbnail + progress +
// remove), mic button (voice ladder), and send. Enter sends; Shift+Enter is a
// newline. Send is blocked while uploads are in flight or a stream is running.

import { useCallback, useRef, useState } from 'react';
import { Button } from '../components/ui.tsx';
import { useVoiceInput } from './useVoiceInput.ts';
import type { Attachment } from './types.ts';

export interface ComposerProps {
  attachments: Attachment[];
  uploading: boolean;
  streaming: boolean;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (localId: string) => void;
  onSend: (text: string) => void;
}

function AttachmentChip({ item, onRemove }: { item: Attachment; onRemove: () => void }) {
  return (
    <span className={`asst-attach${item.phase === 'error' ? ' error' : ''}`}>
      {item.previewUrl ? (
        <img src={item.previewUrl} alt="" className="asst-attach-thumb" />
      ) : (
        <span className="asst-attach-thumb ph" aria-hidden>
          ▤
        </span>
      )}
      <span className="asst-attach-name" title={item.name}>
        {item.name}
      </span>
      {item.phase === 'uploading' && (
        <span
          className="asst-attach-progress"
          role="progressbar"
          aria-valuenow={Math.round(item.progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ width: `${Math.round(item.progress * 100)}%` }} />
        </span>
      )}
      {item.phase === 'error' && <span className="error-text">{item.error}</span>}
      <button type="button" aria-label={`Remove ${item.name}`} onClick={onRemove}>
        ×
      </button>
    </span>
  );
}

export function Composer({
  attachments,
  uploading,
  streaming,
  onAddFiles,
  onRemoveAttachment,
  onSend,
}: ComposerProps) {
  const [text, setText] = useState('');
  const voiceBase = useRef('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const voice = useVoiceInput(
    useCallback((transcript: string) => {
      const base = voiceBase.current;
      const sep = base && !base.endsWith(' ') ? ' ' : '';
      setText(base + sep + transcript);
    }, []),
  );

  const canSend = text.trim().length > 0 && !uploading && !streaming;

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim());
    setText('');
  };

  const onMic = () => {
    if (!voice.listening) voiceBase.current = text; // snapshot so we append, not clobber
    voice.toggle();
  };

  return (
    <div className="assistant-composer">
      {attachments.length > 0 && (
        <div className="asst-attach-row">
          {attachments.map((a) => (
            <AttachmentChip key={a.localId} item={a} onRemove={() => onRemoveAttachment(a.localId)} />
          ))}
        </div>
      )}
      <div className="asst-composer-row">
        <textarea
          aria-label="Message the assistant"
          placeholder="Ask the assistant…"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) onAddFiles(Array.from(e.target.files));
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="btn ghost sm asst-icon-btn"
          aria-label="Attach files"
          title="Attach files"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 11.5l-8 8a5 5 0 0 1-7-7l8.5-8.5a3.2 3.2 0 0 1 4.5 4.5l-8.5 8.5a1.4 1.4 0 0 1-2-2l7.8-7.8" />
          </svg>
        </button>
        {voice.strategy !== 'unavailable' && (
          <button
            type="button"
            className={`btn ghost sm asst-icon-btn${voice.listening ? ' listening' : ''}`}
            aria-label={voice.listening ? 'Stop dictation' : 'Dictate a message'}
            aria-pressed={voice.listening}
            title={voice.error ?? (voice.listening ? 'Stop dictation' : 'Dictate')}
            onClick={onMic}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="9" y="3" width="6" height="11" rx="3" />
              <path d="M6 11.5a6 6 0 0 0 12 0" />
              <path d="M12 17.5V21M9 21h6" />
            </svg>
          </button>
        )}
        <Button variant="primary" size="sm" onClick={submit} disabled={!canSend}>
          Send
        </Button>
      </div>
      {uploading && <div className="asst-composer-note text-dim">Uploading… send unlocks when ready.</div>}
    </div>
  );
}
